import { useState, useEffect, useRef } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { remoteSupabase } from "@/integrations/supabase/remote-client";
import { getGrowthSummary } from "@/lib/growth-summary";

interface YearlyStats {
  year: number;
  shop_pakete: number;
  pre_fba_pakete: number;
  gesamt_pakete: number;
  artikel: number;
}

interface QualityKpis {
  punctuality_rate: number;
  accuracy_rate: number;
  total_shipments: number;
  updated_at: string;
  avg_ticket_resolution_hours: number | null;
}

type TrendDirection = "up" | "down";

interface PreviousWeekKpis {
  punctuality: number | null;
  accuracy: number | null;
  resolution: number | null;
}

const formatNumber = (value: number) =>
  new Intl.NumberFormat("de-DE").format(value);

function AnimatedNumber({ target, isVisible, delay = 0, decimals = 0, formatter }: { target: number; isVisible: boolean; delay?: number; decimals?: number; formatter?: (v: number) => string }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    const timeout = setTimeout(() => {
      const duration = 1800;
      const steps = 60;
      let step = 0;
      const interval = setInterval(() => {
        step++;
        if (step >= steps) {
          setCurrent(target);
          clearInterval(interval);
        } else {
          const progress = step / steps;
          const eased = 1 - Math.pow(1 - progress, 3);
          setCurrent(target * eased);
        }
      }, duration / steps);
      return () => clearInterval(interval);
    }, delay);
    return () => clearTimeout(timeout);
  }, [isVisible, target, delay]);

  if (formatter) {
    return <span className="tabular-nums">{formatter(current)}</span>;
  }
  if (decimals > 0) {
    return <span className="tabular-nums">{current.toFixed(decimals).replace(".", ",")}</span>;
  }
  return <span className="tabular-nums">{formatNumber(Math.round(current))}</span>;
}

export function GrowthChart({ onSummaryChange }: { onSummaryChange?: (summary: ReturnType<typeof getGrowthSummary>) => void }) {
  const [isVisible, setIsVisible] = useState(false);
  const [data, setData] = useState<YearlyStats[]>([]);
  const [qualityKpis, setQualityKpis] = useState<QualityKpis | null>(null);
  const [previousKpis, setPreviousKpis] = useState<PreviousWeekKpis | null>(null);
  const [hoveredYear, setHoveredYear] = useState<number | null>(null);
  const [avgResolutionHours, setAvgResolutionHours] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    remoteSupabase
      .from("yearly_stats")
      .select("year, shop_pakete, pre_fba_pakete, gesamt_pakete, artikel")
      .order("year")
      .then(({ data: rows }) => {
        if (rows) setData(rows as YearlyStats[]);
      });

    const loadQualityKpis = () => {
      remoteSupabase
        .from("quality_kpis")
        .select(
          "punctuality_rate, accuracy_rate, total_shipments, updated_at, avg_ticket_resolution_hours"
        )
        .single()
        .then(({ data: row }) => {
          if (row) {
            setQualityKpis(row as QualityKpis);
          }
        });

      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const weekAgoDate = weekAgo.toLocaleDateString("sv-SE");

      remoteSupabase
        .from("quality_kpi_daily_snapshots")
        .select("punctuality_rate, accuracy_rate, avg_ticket_resolution_hours")
        .eq("snapshot_date", weekAgoDate)
        .maybeSingle()
        .then(({ data: snapshot }) => {
          if (snapshot) {
            setPreviousKpis({
              punctuality: snapshot.punctuality_rate,
              accuracy: snapshot.accuracy_rate,
              resolution: snapshot.avg_ticket_resolution_hours,
            });
          } else {
            setPreviousKpis(null);
          }
        });
    };

    loadQualityKpis();
    const qualityRefresh = window.setInterval(loadQualityKpis, 60_000);

    remoteSupabase
      .from("support_stats_daily")
      .select("snapshot_date, total_resolution_minutes, total_tickets_closed")
      .then(({ data: rows }) => {
        if (rows && rows.length) {
          const totalMinutes = rows.reduce((s: number, r: any) => s + (r.total_resolution_minutes || 0), 0);
          const totalTickets = rows.reduce((s: number, r: any) => s + (r.total_tickets_closed || 0), 0);
          if (totalTickets > 0) {
            setAvgResolutionHours(Math.round((totalMinutes / totalTickets / 60) * 100) / 100);
          }

        }
      });

    return () => window.clearInterval(qualityRefresh);
  }, []);

  const resolutionHours = qualityKpis?.avg_ticket_resolution_hours ?? avgResolutionHours;

  useEffect(() => {
    onSummaryChange?.(getGrowthSummary(data));
  }, [data, onSummaryChange]);

  useEffect(() => {
    if (!data.length) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [data]);

  if (!data.length) return null;

  const maxGesamt = Math.max(...data.map((d) => d.gesamt_pakete));
  const totalPaketeAllTime = data.reduce((sum, d) => sum + d.gesamt_pakete, 0);
  const totalArtikelAllTime = data.reduce((sum, d) => sum + d.artikel, 0);

  const trendDirection = (
    current: number | null | undefined,
    previous: number | null | undefined,
    higherIsBetter: boolean
  ): TrendDirection | undefined => {
    if (current == null || previous == null || current === previous) return undefined;
    const improved = higherIsBetter ? current > previous : current < previous;
    return improved ? "up" : "down";
  };

  const kpiCards = [
    { label: "Pakete insgesamt", value: totalPaketeAllTime },
    { label: "Artikel insgesamt", value: totalArtikelAllTime },
    ...(qualityKpis ? [
      {
        label: "Pünktlichkeitsrate",
        value: qualityKpis.punctuality_rate,
        suffix: "%",
        decimals: 2,
        trend: trendDirection(
          qualityKpis.punctuality_rate,
          previousKpis?.punctuality,
          true
        ),
      },
      {
        label: "Versandgenauigkeit",
        value: qualityKpis.accuracy_rate,
        suffix: "%",
        decimals: 2,
        trend: trendDirection(
          qualityKpis.accuracy_rate,
          previousKpis?.accuracy,
          true
        ),
      },
    ] : []),
    ...(resolutionHours != null
      ? [{
          label: "Ø Support Ticket Resolution",
          value: Math.round(resolutionHours * 60),
          formatter: (v: number) => {
            const rounded = Math.round(v);
            const h = Math.floor(rounded / 60);
            const m = rounded % 60;
            return `${h}:${String(m).padStart(2, '0')} Std`;
          },
          trend: trendDirection(
            resolutionHours,
            previousKpis?.resolution,
            false
          ),
        }]
      : []),
  ];

  return (
    <div ref={ref} className="w-full space-y-8 sm:space-y-10">
      {/* KPI Cards — mobile: single column, sm: 2 cols, lg: row incl. team tile */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4">
        {kpiCards.map((kpi, i) => (
          <div
            key={kpi.label}
            className="text-center p-4 sm:p-6 rounded-2xl bg-card/60 backdrop-blur-sm border border-border/40 shadow-sm"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(24px)",
              transition: `all 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${i * 150}ms`,
            }}
          >
            <div className="flex items-center justify-center gap-2 font-display text-2xl sm:text-3xl font-bold text-primary whitespace-nowrap">
              <AnimatedNumber
                target={'decimals' in kpi ? kpi.value : ('suffix' in kpi ? Math.round(kpi.value * 100) / 100 : kpi.value)}
                isVisible={isVisible}
                delay={i * 200}
                decimals={'decimals' in kpi ? (kpi as any).decimals : 0}
                formatter={'formatter' in kpi ? (kpi as any).formatter : undefined}
              />
              {'formatter' in kpi ? null : 'suffix' in kpi && <span>{(kpi as any).suffix}</span>}
              {'trend' in kpi && (kpi as any).trend && (
                <span
                  className={`text-xl sm:text-2xl ${(kpi as any).trend === "up" ? "text-primary" : "text-destructive"}`}
                  aria-label={(kpi as any).trend === "up" ? "Verbessert" : "Verschlechtert"}
                  title={(kpi as any).trend === "up" ? "Verbessert" : "Verschlechtert"}
                >
                  {(kpi as any).trend === "up" ? (
                    <ArrowUp size={24} strokeWidth={3} aria-hidden="true" />
                  ) : (
                    <ArrowDown size={24} strokeWidth={3} aria-hidden="true" />
                  )}
                </span>
              )}
            </div>
            <div className="text-base text-muted-foreground mt-1.5">{kpi.label}</div>
          </div>
        ))}

        {/* Team tile — sixth KPI card */}
        <div
          className="text-center p-4 sm:p-6 rounded-2xl bg-card/60 backdrop-blur-sm border border-border/40 shadow-sm"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(24px)",
            transition: `all 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${kpiCards.length * 150}ms`,
          }}
        >
          <div className="font-display text-2xl sm:text-3xl font-bold text-primary">
            9
          </div>
          <div className="text-base text-muted-foreground mt-1.5">
            Festangestellte
          </div>
          <div className="text-sm text-muted-foreground mt-1">
            2 Büro / 7 Lager
          </div>
        </div>
      </div>

      {/* MOBILE: Horizontal bars */}
      <div className="block sm:hidden space-y-2">
        {data.map((d, i) => {
          const shopWidth = (d.shop_pakete / maxGesamt) * 100;
          const preFbaWidth = (d.pre_fba_pakete / maxGesamt) * 100;
          const shopPct = d.gesamt_pakete > 0 ? Math.round((d.shop_pakete / d.gesamt_pakete) * 100) : 0;
          const preFbaPct = d.gesamt_pakete > 0 ? Math.round((d.pre_fba_pakete / d.gesamt_pakete) * 100) : 0;
          const isHovered = hoveredYear === d.year;

          return (
            <div
              key={d.year}
              className="relative rounded-xl transition-colors duration-200"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateX(0)" : "translateX(-20px)",
                transition: `opacity 0.5s ease-out ${i * 80 + 300}ms, transform 0.5s ease-out ${i * 80 + 300}ms`,
                backgroundColor: isHovered ? "hsl(var(--accent))" : "transparent",
              }}
              onClick={() => setHoveredYear(hoveredYear === d.year ? null : d.year)}
            >
              <div className="flex items-center gap-2 px-2 py-2">
                <span className="w-10 text-sm shrink-0 text-muted-foreground tabular-nums">{d.year}</span>
                <div className="flex-1 h-7 bg-muted/40 rounded-lg overflow-hidden relative">
                  <div
                    className="absolute inset-y-0 left-0 transition-all duration-[1200ms] ease-out"
                    style={{
                      width: isVisible ? `${shopWidth}%` : "0%",
                      transitionDelay: `${i * 80 + 500}ms`,
                      background: `linear-gradient(90deg, hsl(var(--primary)) 0%, hsl(var(--emoose-green-light)) 100%)`,
                      opacity: 0.85,
                      borderRadius: d.pre_fba_pakete > 0 ? "0.5rem 0 0 0.5rem" : "0.5rem",
                    }}
                  />
                  {d.pre_fba_pakete > 0 && (
                    <div
                      className="absolute inset-y-0 rounded-r-lg transition-all duration-[1200ms] ease-out"
                      style={{
                        left: isVisible ? `${shopWidth}%` : "0%",
                        width: isVisible ? `${preFbaWidth}%` : "0%",
                        background: `hsl(var(--accent-foreground))`,
                        opacity: 0.75,
                        transitionDelay: `${i * 80 + 700}ms`,
                      }}
                    />
                  )}
                  {/* YoY growth badge inside bar */}
                  {i > 0 && (d.year !== new Date().getFullYear() || d.gesamt_pakete > data[i - 1].gesamt_pakete) && (() => {
                    const yoyPct = ((d.gesamt_pakete - data[i - 1].gesamt_pakete) / data[i - 1].gesamt_pakete * 100);
                    return (
                      <span
                        className={`absolute inset-y-0 left-2 flex items-center text-xs font-bold tabular-nums drop-shadow-sm ${
                          yoyPct >= 0 ? "text-white" : "text-destructive"
                        }`}
                        style={{
                          opacity: isVisible ? 1 : 0,
                          transition: `opacity 0.5s ease-out ${i * 80 + 1000}ms`,
                        }}
                      >
                        {yoyPct >= 0 ? "+" : ""}{yoyPct.toFixed(1)}%
                      </span>
                    );
                  })()}
                </div>
                <span className="text-sm text-muted-foreground w-16 text-right tabular-nums shrink-0">
                  {formatNumber(d.gesamt_pakete)}
                </span>
              </div>

              {/* Mobile tooltip (inline expand) */}
              {isHovered && (
                <div className="px-12 pb-2">
                  <div className="text-xs space-y-0.5 text-muted-foreground">
                    {i > 0 && (d.year !== new Date().getFullYear() || d.gesamt_pakete > data[i - 1].gesamt_pakete) && (() => {
                      const yoyPct = ((d.gesamt_pakete - data[i - 1].gesamt_pakete) / data[i - 1].gesamt_pakete * 100);
                      return (
                        <div className="flex justify-between">
                          <span>gg. Vorjahr</span>
                          <span className={`font-medium ${yoyPct >= 0 ? "text-primary" : "text-destructive"}`}>
                            {yoyPct >= 0 ? "+" : ""}{yoyPct.toFixed(1)}%
                          </span>
                        </div>
                      );
                    })()}
                    <div className="flex justify-between">
                      <span>Shop</span>
                      <span className="text-foreground">{formatNumber(d.shop_pakete)} ({shopPct}%)</span>
                    </div>
                    {d.pre_fba_pakete > 0 && (
                      <div className="flex justify-between">
                        <span>PRE-FBA</span>
                        <span className="text-foreground">{formatNumber(d.pre_fba_pakete)} ({preFbaPct}%)</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Artikel</span>
                      <span className="text-foreground">{formatNumber(d.artikel)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* DESKTOP: Vertical bars */}
      <div className="hidden sm:block">
        <div className="flex items-end gap-3 lg:gap-4 justify-center relative" style={{ minHeight: 280 }}>
          {data.map((d, i) => {
            const shopHeight = (d.shop_pakete / maxGesamt) * 100;
            const preFbaHeight = (d.pre_fba_pakete / maxGesamt) * 100;
            const shopPct = d.gesamt_pakete > 0 ? Math.round((d.shop_pakete / d.gesamt_pakete) * 100) : 0;
            const preFbaPct = d.gesamt_pakete > 0 ? Math.round((d.pre_fba_pakete / d.gesamt_pakete) * 100) : 0;
            const isHovered = hoveredYear === d.year;
            const isFirst = i <= 1;
            const isLast = i >= data.length - 2;
            const tooltipAlign = isFirst ? "left-0" : isLast ? "right-0" : "left-1/2 -translate-x-1/2";

            return (
              <div
                key={d.year}
                className="flex flex-col items-center relative group"
                style={{ flex: "1 1 0", maxWidth: 120, minWidth: 48 }}
                onMouseEnter={() => setHoveredYear(d.year)}
                onMouseLeave={() => setHoveredYear(null)}
              >
                {/* Number + YoY above bar */}
                <div
                  className="flex flex-col items-center mb-1"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transition: `opacity 0.5s ease-out ${i * 80 + 800}ms`,
                  }}
                >
                  {i > 0 && (d.year !== new Date().getFullYear() || d.gesamt_pakete > data[i - 1].gesamt_pakete) && (() => {
                    const yoyPct = ((d.gesamt_pakete - data[i - 1].gesamt_pakete) / data[i - 1].gesamt_pakete * 100);
                    return (
                      <span className={`text-[11px] font-semibold tabular-nums ${yoyPct >= 0 ? "text-primary" : "text-destructive"}`}>
                        {yoyPct >= 0 ? "+" : ""}{yoyPct.toFixed(1)}%
                      </span>
                    );
                  })()}
                  <span className="text-sm text-muted-foreground tabular-nums">
                    {formatNumber(d.gesamt_pakete)}
                  </span>
                </div>

                {/* Bar */}
                <div
                  className="w-full rounded-t-lg overflow-hidden relative bg-muted/40"
                  style={{
                    height: isVisible ? `${Math.max((d.gesamt_pakete / maxGesamt) * 220, 8)}px` : "0px",
                    transition: `height 1.2s cubic-bezier(0.16, 1, 0.3, 1) ${i * 80 + 500}ms`,
                  }}
                >
                  <div
                    className="absolute bottom-0 left-0 right-0"
                    style={{
                      height: `${shopHeight / (shopHeight + preFbaHeight) * 100}%`,
                      background: `linear-gradient(0deg, hsl(var(--primary)) 0%, hsl(var(--emoose-green-light)) 100%)`,
                      opacity: isHovered ? 1 : 0.85,
                      borderRadius: d.pre_fba_pakete > 0 ? "0" : "0.5rem 0.5rem 0 0",
                    }}
                  />
                  {d.pre_fba_pakete > 0 && (
                    <div
                      className="absolute top-0 left-0 right-0 rounded-t-lg"
                      style={{
                        height: `${preFbaHeight / (shopHeight + preFbaHeight) * 100}%`,
                        background: `hsl(var(--accent-foreground))`,
                        opacity: isHovered ? 1 : 0.75,
                      }}
                    />
                  )}
                </div>

                {/* Year label */}
                <span className="text-sm text-muted-foreground tabular-nums mt-2">{d.year}</span>

                {/* Tooltip */}
                {isHovered && (
                  <div className={`absolute top-full mt-1 z-50 bg-card/95 backdrop-blur-md border border-border/60 rounded-xl px-4 py-3 shadow-lg pointer-events-none whitespace-nowrap ${tooltipAlign}`}>
                    <div className="text-sm space-y-1">
                      {i > 0 && (d.year !== new Date().getFullYear() || d.gesamt_pakete > data[i - 1].gesamt_pakete) && (() => {
                        const yoyPct = ((d.gesamt_pakete - data[i - 1].gesamt_pakete) / data[i - 1].gesamt_pakete * 100);
                        return (
                          <div className="flex justify-between gap-4 pb-1 border-b border-border/40">
                            <span className="text-muted-foreground">gg. Vorjahr</span>
                            <span className={`font-medium tabular-nums ${yoyPct >= 0 ? "text-primary" : "text-destructive"}`}>
                              {yoyPct >= 0 ? "+" : ""}{yoyPct.toFixed(1)}%
                            </span>
                          </div>
                        );
                      })()}
                      <div className="flex justify-between gap-4">
                        <span className="text-muted-foreground">Shop</span>
                        <span className="font-medium text-foreground tabular-nums">{formatNumber(d.shop_pakete)} ({shopPct}%)</span>
                      </div>
                      {d.pre_fba_pakete > 0 && (
                        <div className="flex justify-between gap-4">
                          <span className="text-muted-foreground">PRE-FBA</span>
                          <span className="font-medium text-foreground tabular-nums">{formatNumber(d.pre_fba_pakete)} ({preFbaPct}%)</span>
                        </div>
                      )}
                      <div className="flex justify-between gap-4 pt-1 border-t border-border/40">
                        <span className="text-muted-foreground">Artikel</span>
                        <span className="font-medium text-foreground tabular-nums">{formatNumber(d.artikel)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-6 pt-2">
        {[
          { label: "Shop-Pakete", className: "bg-primary" },
          { label: "PRE-FBA-Pakete", className: "bg-accent-foreground" },
        ].map((item) => (
          <div key={item.label} className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className={`w-3 h-3 rounded-full ${item.className}`} />
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
}
